import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography,
  Box, TextField, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, IconButton, Grid, Chip, Tooltip, CircularProgress, MenuItem, Select,
  Stack
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SaveIcon from '@mui/icons-material/Save';
import PaymentIcon from '@mui/icons-material/Payment';
import useSnackbar from '../../hooks/useSnackbar';
import { getPaymentSchedule, upsertPaymentSchedule, updateInstallmentStatus } from '../../api/paymentScheduleAPI';

const PaymentScheduleModal = ({ open, onClose, queryData }) => {
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [scheduleData, setScheduleData] = useState(null);

  // Form states
  const [totalAmount, setTotalAmount] = useState(0);
  const [advancePayment, setAdvancePayment] = useState(0);
  const [advanceDate, setAdvanceDate] = useState('');
  const [advancePaymentLink, setAdvancePaymentLink] = useState('');
  const [installments, setInstallments] = useState([]);
  
  // Ad-hoc Payment states
  const [adHocDialog, setAdHocDialog] = useState(false);
  const [adHocForm, setAdHocForm] = useState({ amount: '', date: '', remarks: '' });

  useEffect(() => {
    if (open && queryData) {
      const qCost = Number(queryData.op_price) || Number(queryData.cost) || Number(queryData.price) || 0;
      const qAdv = (Array.isArray(queryData.op_advance) && queryData.op_advance.length > 0
        ? queryData.op_advance.reduce((s, a) => s + (Number(a) || 0), 0)
        : null) ?? Number(queryData.advance) ?? 0;

      setTotalAmount(qCost || 0);
      setAdvancePayment(qAdv || 0);
      setAdvanceDate(new Date().toISOString().split('T')[0]); // Default to today
      setAdvancePaymentLink('');
      setInstallments([]);
      fetchSchedule(qCost, qAdv);
    }
  }, [open, queryData]);

  const fetchSchedule = async (fallbackCost, fallbackAdv) => {
    if (!queryData?._id) return;
    const qCostFallback = fallbackCost ?? (Number(queryData.op_price) || Number(queryData.cost) || Number(queryData.price) || 0);
    const qAdvFallback = fallbackAdv ?? ((Array.isArray(queryData.op_advance) && queryData.op_advance.length > 0
      ? queryData.op_advance.reduce((s, a) => s + (Number(a) || 0), 0)
      : null) ?? Number(queryData.advance) ?? 0);

    setLoading(true);
    try {
      const res = await getPaymentSchedule(queryData._id);
      if (res && res.data) {
        const s = res.data;
        setScheduleData(s);
        const instSum = (s.installments || []).reduce((acc, curr) => acc + (Number(curr.amount) || 0), 0);
        const psTotal = Number(s.total_amount) || 0;
        const psAdv = Number(s.advance_payment) || 0;

        if (qCostFallback > 0 && psTotal > 0 && Math.abs(psTotal - (psAdv + instSum)) >= 1 && Math.abs(psTotal - qCostFallback) >= 1) {
          setTotalAmount(qCostFallback);
          setAdvancePayment(qAdvFallback > 0 ? qAdvFallback : psAdv);
        } else {
          setTotalAmount(psTotal > 0 ? psTotal : (qCostFallback || ''));
          setAdvancePayment(s.advance_payment !== undefined && s.advance_payment !== null ? s.advance_payment : (qAdvFallback || ''));
        }

        setAdvanceDate(s.advance_date ? new Date(s.advance_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]);
        setAdvancePaymentLink(s.advance_payment_link || '');
        setInstallments(s.installments || []);
      }
    } catch (err) {
      console.error("Failed to fetch schedule:", err);
      showSnackbar("Failed to load payment schedule", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleAddInstallment = () => {
    setInstallments([...installments, { amount: '', due_date: '', status: 'Pending', payment_link: '', remarks: '' }]);
  };

  const handleRemoveInstallment = (index) => {
    const updated = [...installments];
    updated.splice(index, 1);
    setInstallments(updated);
  };

  const handleInstallmentChange = (index, field, value) => {
    const updated = [...installments];
    updated[index][field] = value;
    setInstallments(updated);
  };

  const handleOpenAdHoc = () => {
    setAdHocForm({ amount: '', date: new Date().toISOString().split('T')[0], remarks: 'Ad-hoc Payment' });
    setAdHocDialog(true);
  };

  const handleRecordAdHocPayment = () => {
    let paymentAmount = Number(adHocForm.amount);
    if (!paymentAmount || paymentAmount <= 0) return showSnackbar("Enter a valid amount", "error");

    let updatedInstallments = [...installments];
    
    // Process only Pending installments, strictly in chronological order
    const pendingIndices = updatedInstallments
      .map((inst, idx) => ({ ...inst, idx }))
      .filter(inst => inst.status === 'Pending')
      .sort((a, b) => new Date(a.due_date || 0) - new Date(b.due_date || 0))
      .map(inst => inst.idx);

    for (let idx of pendingIndices) {
      if (paymentAmount <= 0) break;
      const inst = updatedInstallments[idx];
      const instAmt = Number(inst.amount) || 0;

      if (paymentAmount >= instAmt) {
        paymentAmount -= instAmt;
        inst.amount = 0; // Will filter out later
      } else {
        inst.amount = instAmt - paymentAmount;
        paymentAmount = 0;
      }
    }

    // Filter out 0 amount pending installments
    updatedInstallments = updatedInstallments.filter(inst => !(inst.status === 'Pending' && Number(inst.amount) <= 0));

    // Add the new ad-hoc payment
    updatedInstallments.push({
      amount: adHocForm.amount,
      due_date: adHocForm.date,
      status: 'Paid',
      remarks: adHocForm.remarks,
      payment_link: ''
    });

    // Sort all by due_date to keep it tidy
    updatedInstallments.sort((a, b) => new Date(a.due_date || 0) - new Date(b.due_date || 0));

    setInstallments(updatedInstallments);
    setAdHocDialog(false);
    showSnackbar("Ad-hoc payment logged. Please 'Save Schedule' to apply.", "success");
  };

  const handleSave = async () => {
    if (!queryData?._id) return;
    
    // Basic validation
    if (advancePayment === '' || advancePayment === null || advancePayment === undefined || !advanceDate) {
      return showSnackbar("Advance payment amount and date are required", "error");
    }

    let isValid = true;
    installments.forEach(inst => {
      if (inst.amount === '' || inst.amount === null || inst.amount === undefined || !inst.due_date) {
        isValid = false;
      }
    });  
    
    if (!isValid) {
      return showSnackbar("All installments must have an amount and due date", "error");
    }

    const payload = {
      query_id: queryData._id,
      total_amount: totalAmount,
      advance_payment: advancePayment,
      advance_date: advanceDate,
      advance_payment_link: advancePaymentLink,
      installments
    };

    setLoading(true);
    try {
      const res = await upsertPaymentSchedule(payload);
      if (res.success) {
        showSnackbar("Payment schedule saved successfully", "success");
        setScheduleData(res.data);
        onClose();
      }
    } catch (err) {
      console.error("Failed to save schedule:", err);
      showSnackbar("Failed to save payment schedule", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (installmentId, newStatus) => {
    if (!scheduleData?._id) return;
    setLoading(true);
    try {
      const payload = { status: newStatus };
      if (newStatus === 'Paid') payload.paid_date = new Date().toISOString();
      else payload.paid_date = null;

      const res = await updateInstallmentStatus(scheduleData._id, installmentId, payload);
      if (res.success) {
        showSnackbar(`Installment marked as ${newStatus}`, "success");
        fetchSchedule(); // refresh
      }
    } catch (err) {
      console.error("Failed to update status:", err);
      showSnackbar("Failed to update installment status", "error");
    } finally {
      setLoading(false);
    }
  };

  const calculateDue = () => {
    const instTotal = installments.reduce((acc, curr) => acc + (Number(curr.amount) || 0), 0);
    return Number(totalAmount) - Number(advancePayment) - instTotal;
  };

  const calculateTotalPaid = () => {
    const paidInstTotal = installments
      .filter(inst => inst.status === 'Paid')
      .reduce((acc, curr) => acc + (Number(curr.amount) || 0), 0);
    return Number(advancePayment) + paidInstTotal;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" fontWeight="bold">
          Payment Schedule: {queryData?.guest_info?.guest_name || "Guest"}
        </Typography>
        {loading && <CircularProgress size={24} />}
      </DialogTitle>
      
      <DialogContent dividers>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ color: 'primary.main' }}>
              Initial Payment Details
            </Typography>
            {queryData?.travel_date && (
              <Chip 
                label={`Travel Date: ${new Date(queryData.travel_date).toLocaleDateString('en-GB')}`} 
                color="info" 
                size="small" 
                variant="outlined"
              />
            )}
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Total Amount"
                type="number"
                fullWidth
                size="small"
                value={totalAmount}
                onChange={(e) => setTotalAmount(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Advance Payment (First)"
                type="number"
                fullWidth
                size="small"
                value={advancePayment}
                onChange={(e) => setAdvancePayment(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Advance Date"
                type="date"
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                value={advanceDate}
                onChange={(e) => setAdvanceDate(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Advance Payment Link (Optional)"
                fullWidth
                size="small"
                value={advancePaymentLink}
                onChange={(e) => setAdvancePaymentLink(e.target.value)}
              />
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1, border: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" fontWeight="bold">Total Amount: ₹{totalAmount}</Typography>
            <Typography variant="body2" fontWeight="bold" color="success.main">Advance: ₹{advancePayment}</Typography>
            <Typography variant="body2" fontWeight="bold" color="info.main">Total Paid: ₹{calculateTotalPaid()}</Typography>
            <Typography variant="body2" fontWeight="bold" color={calculateDue() < 0 ? 'error.main' : 'warning.main'}>
              Unscheduled Due: ₹{calculateDue()}
            </Typography>
          </Box>
        </Box>

        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ color: 'primary.main' }}>
              Installment Schedule
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button startIcon={<PaymentIcon />} variant="contained" color="success" size="small" onClick={handleOpenAdHoc}>
                Log Ad-hoc Payment
              </Button>
              <Button startIcon={<AddCircleOutlineIcon />} variant="outlined" size="small" onClick={handleAddInstallment}>
                Add Installment
              </Button>
            </Stack>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead sx={{ bgcolor: 'action.hover' }}>
                <TableRow>
                  <TableCell>Amount</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Payment Link (Opt)</TableCell>
                  <TableCell>Remarks</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {installments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        No installments added yet. Click "Add Installment" to schedule due payments.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  installments.map((inst, index) => {
                    const isExisting = inst._id !== undefined;
                    return (
                      <TableRow key={inst._id || index}>
                        <TableCell>
                          <TextField 
                            size="small" 
                            type="number" 
                            value={inst.amount} 
                            onChange={(e) => handleInstallmentChange(index, 'amount', e.target.value)} 
                            fullWidth 
                            disabled={inst.status === 'Paid'}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField 
                            size="small" 
                            type="date" 
                            value={inst.due_date ? new Date(inst.due_date).toISOString().split('T')[0] : ''} 
                            onChange={(e) => handleInstallmentChange(index, 'due_date', e.target.value)} 
                            fullWidth 
                            disabled={inst.status === 'Paid'}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField 
                            size="small" 
                            value={inst.payment_link || ''} 
                            onChange={(e) => handleInstallmentChange(index, 'payment_link', e.target.value)} 
                            fullWidth 
                            disabled={inst.status === 'Paid'}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField 
                            size="small" 
                            value={inst.remarks || ''} 
                            onChange={(e) => handleInstallmentChange(index, 'remarks', e.target.value)} 
                            fullWidth 
                          />
                        </TableCell>
                        <TableCell align="center">
                          {isExisting ? (
                            <Select
                              size="small"
                              value={inst.status}
                              onChange={(e) => handleStatusUpdate(inst._id, e.target.value)}
                              sx={{
                                width: 120,
                                fontSize: '0.8rem',
                                color: inst.status === 'Paid' ? 'success.main' : 'warning.main',
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: inst.status === 'Paid' ? 'success.main' : 'warning.main',
                                }
                              }}
                            >
                              <MenuItem value="Pending">Pending</MenuItem>
                              <MenuItem value="Paid">Paid</MenuItem>
                            </Select>
                          ) : (
                            <Chip label="Unsaved" size="small" color="default" />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Remove">
                            <span>
                              <IconButton 
                                size="small" 
                                color="error" 
                                onClick={() => handleRemoveInstallment(index)}
                                disabled={inst.status === 'Paid'}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
                </TableBody>
              </Table>
            </TableContainer>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={loading}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" color="primary" startIcon={<SaveIcon />} disabled={loading}>
          Save Schedule
        </Button>
      </DialogActions>

      {/* Ad-Hoc Payment Dialog */}
      <Dialog open={adHocDialog} onClose={() => setAdHocDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Log Ad-hoc Payment</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Enter an early or ad-hoc payment amount. This will automatically deduct from the next pending installments chronologically, keeping your total schedule balanced!
            </Typography>
          </Box>
          <Stack spacing={2}>
            <TextField
              label="Amount Received (₹)"
              type="number"
              fullWidth
              size="small"
              value={adHocForm.amount}
              onChange={(e) => setAdHocForm(f => ({ ...f, amount: e.target.value }))}
            />
            <TextField
              label="Payment Date"
              type="date"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              value={adHocForm.date}
              onChange={(e) => setAdHocForm(f => ({ ...f, date: e.target.value }))}
            />
            <TextField
              label="Remarks"
              fullWidth
              size="small"
              value={adHocForm.remarks}
              onChange={(e) => setAdHocForm(f => ({ ...f, remarks: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdHocDialog(false)} color="inherit">Cancel</Button>
          <Button onClick={handleRecordAdHocPayment} variant="contained" color="success">
            Log Payment
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default PaymentScheduleModal;
